"use client";

import { useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";

import { Link } from "@/navigation";

import "swiper/css";
import "swiper/css/navigation";

const IMAGE_BASE = "/image/new-version/casting/Die-Casting/Control-System";

const STEP_COUNT = 8;

const QC_IMAGES = [
	`${IMAGE_BASE}/measuring-machine-1.webp`,
	`${IMAGE_BASE}/cmm.webp`,
	`${IMAGE_BASE}/contour-measuring-instrument-8.JPG`,
	`${IMAGE_BASE}/altimeter.webp`,
] as const;

const ISO_LOGOS = [
	{ src: `${IMAGE_BASE}/iso-9001.png`, label: "ISO 9001:2015" },
	{ src: `${IMAGE_BASE}/iso-13485.png`, label: "ISO 13485:2016" },
	{ src: `${IMAGE_BASE}/iso-14001.png`, label: "ISO 14001:2015" },
	{ src: `${IMAGE_BASE}/iatf.png`, label: "IATF 16949" },
] as const;

const CONTENT_BLOCKS = [
	{ titleKey: "qcAdvancedTitle", descKey: "qcAdvancedDesc" },
	{ titleKey: "qcCertifiedTitle", descKey: "qcCertifiedDesc" },
] as const;

export default function DieCastingQualityInspection() {
	const t = useTranslations("new-die-casting");
	const swiperRef = useRef<SwiperType | null>(null);
	const [isBeginning, setIsBeginning] = useState(true);
	const [isEnd, setIsEnd] = useState(false);

	const handleSlideChange = (swiper: SwiperType) => {
		setIsBeginning(swiper.isBeginning);
		setIsEnd(swiper.isEnd);
	};

	return (
		<section className="bg-[#f6ffde] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<header className="mb-6 text-center sm:mb-8 md:mb-10 lg:mb-12">
					<h2 className="text-2xl font-bold leading-tight text-black sm:text-[30px] md:text-4xl">
						{t("qcTitle")}
					</h2>
					<p className="mt-3 text-sm leading-relaxed text-black sm:mt-4 md:text-base">{t("qcDesc")}</p>
				</header>

				<div className="relative mb-8 sm:mb-10 md:mb-12">
					<Swiper
						modules={[Navigation]}
						spaceBetween={12}
						slidesPerView={2}
						breakpoints={{
							480: { slidesPerView: 3 },
							768: { slidesPerView: 4 },
							1024: { slidesPerView: 5 },
						}}
						onSwiper={(swiper) => {
							swiperRef.current = swiper;
						}}
						onSlideChange={handleSlideChange}
					>
						{Array.from({ length: STEP_COUNT }, (_, i) => {
							const num = i + 1;
							const padded = String(num).padStart(2, "0");
							return (
								<SwiperSlide key={num}>
									<div className="flex h-full flex-col items-center rounded-lg border border-gray-200 bg-white px-4 py-6 text-center shadow-card sm:px-5 sm:py-8">
										<span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#f2f4e9] text-lg font-bold text-themePrimary400 sm:h-14 sm:w-14 sm:text-2xl">
											{padded}
										</span>
										<h3 className="mb-2 text-sm font-bold text-[#64a525] sm:text-base">
											{t(`qcStep${num}Title`)}
										</h3>
										<p className="text-xs leading-relaxed text-black sm:text-sm">{t(`qcStep${num}Desc`)}</p>
									</div>
								</SwiperSlide>
							);
						})}
					</Swiper>

					<button
						type="button"
						onClick={() => swiperRef.current?.slidePrev()}
						disabled={isBeginning}
						className={`absolute left-0 top-1/2 z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-md transition-colors sm:h-10 sm:w-10 ${
							isBeginning ? "cursor-not-allowed text-gray-300" : "text-themePrimary400 hover:text-[#83b751]"
						}`}
						aria-label="Previous"
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M15 18L9 12L15 6"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>

					<button
						type="button"
						onClick={() => swiperRef.current?.slideNext()}
						disabled={isEnd}
						className={`absolute right-0 top-1/2 z-10 flex h-9 w-9 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-white shadow-md transition-colors sm:h-10 sm:w-10 ${
							isEnd ? "cursor-not-allowed text-gray-300" : "text-themePrimary400 hover:text-[#83b751]"
						}`}
						aria-label="Next"
					>
						<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M9 18L15 12L9 6"
								stroke="currentColor"
								strokeWidth="2"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</button>
				</div>

				<div className="grid grid-cols-1 items-start gap-8 lg:grid-cols-5">
					<div className="lg:col-span-2">
						<div className="grid grid-cols-2 gap-2">
							{QC_IMAGES.map((src, index) => (
								<div key={src} className="relative aspect-[4/2.2] overflow-hidden bg-white">
									<Image
										src={src}
										alt={t(`qcImageAlt${index + 1}`)}
										fill
										className="object-cover"
										sizes="(max-width: 1024px) 50vw, 25vw"
									/>
								</div>
							))}
						</div>

						<div className="mt-6 flex items-center justify-between gap-6 sm:mt-8 sm:gap-8 md:gap-10">
							{ISO_LOGOS.map((logo) => (
								<div key={logo.label} className="relative h-16 w-16 sm:h-20 sm:w-20">
									<Image src={logo.src} alt={logo.label} fill className="object-contain" sizes="80px" />
								</div>
							))}
						</div>
					</div>

					<div className="flex flex-col gap-6 sm:gap-8 lg:col-span-3">
						{CONTENT_BLOCKS.map((block) => (
							<div key={block.titleKey}>
								<h3 className="mb-2 text-base font-bold text-themePrimary400 sm:mb-3 sm:text-lg md:text-xl">
									{t(block.titleKey)}
								</h3>
								<p className="text-sm leading-relaxed text-black md:text-base">{t(block.descKey)}</p>
							</div>
						))}

						<div>
							<Link
								href="/upload"
								className="inline-block rounded-lg bg-themePrimary400 px-8 py-3 text-sm font-semibold text-white transition-colors hover:bg-themePrimary400/85 sm:text-base"
							>
								{t("qcCtaButton")}
							</Link>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
}
